// Newsletter domain types — mirrors the NewsletterResource, NewsletterAudienceResource,
// NewsletterRecipientCountResource, CreditBalanceResource, and CreditLedgerResource shapes
// from the backend handoff.

export const NewsletterStatus = {
  Draft:     'draft',
  Scheduled: 'scheduled',
  Sending:   'sending',
  Sent:      'sent',
  Failed:    'failed',
} as const

export type NewsletterStatus = typeof NewsletterStatus[keyof typeof NewsletterStatus]

export const NewsletterType = {
  Briefing:   'briefing',
  Debriefing: 'debriefing',
  Newsletter: 'newsletter',
  RaceUpdate: 'race_update',
} as const

export type NewsletterType = typeof NewsletterType[keyof typeof NewsletterType]

export const NewsletterSeedType = {
  Event:   'event',
  Edition: 'edition',
  Race:    'race',
} as const

export type NewsletterSeedType = typeof NewsletterSeedType[keyof typeof NewsletterSeedType]

export const CreditLedgerType = {
  Earned:    'earned',
  Purchased: 'purchased',
  Consumed:  'consumed',
  Refunded:  'refunded',
  Adjusted:  'adjusted',
} as const

export type CreditLedgerType = typeof CreditLedgerType[keyof typeof CreditLedgerType]

export interface ManualAddition {
  email: string
  name: string | null
}

export interface NewsletterAudience {
  seed_type: NewsletterSeedType
  seed_id: number
  event_name: string | null
  edition_name: string | null
  race_name: string | null
  registration_statuses: string[]
  manual_additions: ManualAddition[]
  manual_exclusions: number[]
}

export interface Newsletter {
  id: number
  organisation_id: number
  title: string
  type: NewsletterType
  subject: string | null
  body: string | null
  status: NewsletterStatus
  scheduled_at: string | null
  sent_at: string | null
  recipient_count: number | null
  test_send_count: number
  test_sends_remaining: number
  failure_reason: string | null
  audience: NewsletterAudience | null
  created_at: string
  updated_at: string
}

export interface RecipientCount {
  total: number
  from_registrations: number
  manual_additions: number
  suppressed: number
  excluded: number
}

export interface CreditBalance {
  organisation_id: number
  balance: number
  price_per_credit: number
  currency: string
}

// reference_type is a stable morph alias: 'registration' | 'newsletter' | 'credit_purchase'
export type CreditReferenceType = 'registration' | 'newsletter' | 'credit_purchase'

export interface CreditLedgerEntry {
  id: number
  amount: number
  type: CreditLedgerType
  description: string | null
  reference_type: CreditReferenceType | null
  reference_id: number | null
  created_at: string
}

export interface SendNewsletterSuccess {
  message: string
  recipient_count: number
  credits_charged: number
  balance_after: number
}

export interface InsufficientCreditsError {
  message: string
  required: number
  available: number
}

export interface CreditPurchaseIntent {
  client_secret: string
  stripe_publishable_key: string
  amount: number
  quantity: number
}

export interface StoreNewsletterPayload {
  title: string
  type: NewsletterType
  subject: string
  body: string
}

export interface UpdateNewsletterPayload {
  title?: string
  type?: NewsletterType
  subject?: string
  body?: string
}

export interface UpsertAudiencePayload {
  seed_type: NewsletterSeedType
  seed_id: number
  registration_statuses: string[]
  manual_additions: ManualAddition[]
  manual_exclusions: number[]
}

export interface SendNewsletterPayload {
  scheduled_at?: string | null
}
